/**
 * Token Price Service
 * Fetches real-time token prices from on-chain LP pools
 */

import { Interface, formatUnits, JsonRpcProvider } from 'ethers'
import {
  CHAIN_PRICE_CONFIGS,
  TOKEN_PRICE_CONFIGS,
  PRICE_FEEDS,
  ChainPriceConfig,
} from '@/lib/tokenPriceConfig'
import { getChainProvider, clearProviderCache } from '@/lib/chainProvider'
import { getMulticall3Address } from '@/lib/chains'
import { createMulticallService, MulticallService } from './multicallService'

// ============================================================================
// ABIs for on-chain price lookups
// ============================================================================

// Uniswap V2 / Aerodrome style pool ABI
const V2_POOL_ABI = [
  'function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
  'function token0() external view returns (address)',
  'function token1() external view returns (address)',
]

// Uniswap V3 / PancakeSwap V3 pool ABI
const V3_POOL_ABI = [
  'function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)',
  'function token0() external view returns (address)',
  'function token1() external view returns (address)',
  'function liquidity() external view returns (uint128)',
]

// Aerodrome CL pool ABI (different slot0 signature - no feeProtocol)
const AERODROME_CL_POOL_ABI = [
  'function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, bool unlocked)',
  'function token0() external view returns (address)',
  'function token1() external view returns (address)',
  'function liquidity() external view returns (uint128)',
]

// PancakeSwap V3 Quoter ABI
const V3_QUOTER_ABI = [
  'function quoteExactInputSingle(tuple(address tokenIn, address tokenOut, uint256 amountIn, uint24 fee, uint160 sqrtPriceLimitX96) params) external returns (uint256 amountOut, uint160 sqrtPriceX96After, uint32 initializedTicksCrossed, uint256 gasEstimate)',
]

// Chainlink Price Feed ABI
const CHAINLINK_ABI = [
  'function latestRoundData() external view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)',
  'function decimals() external view returns (uint8)',
]

// ============================================================================
// Price Cache
// ============================================================================

interface PriceCache {
  price: number
  timestamp: number
}

const priceCache: Map<string, PriceCache> = new Map()
const CACHE_TTL_MS = 30000 // 30 seconds

function getCacheKey(symbol: string, chainId: number): string {
  return `${symbol}-${chainId}`
}

function getCachedPrice(symbol: string, chainId: number): number | null {
  const key = getCacheKey(symbol, chainId)
  const cached = priceCache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.price
  }
  return null
}

function setCachedPrice(symbol: string, chainId: number, price: number): void {
  const key = getCacheKey(symbol, chainId)
  priceCache.set(key, { price, timestamp: Date.now() })
}


// ============================================================================
// Native Token Price (ETH, BNB -> USD)
// ============================================================================

export async function getNativeTokenPriceUsd(chainId: number): Promise<number> {
  const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
  if (!chainConfig) {
    throw new Error(`Unknown chain: ${chainId}`)
  }

  // Check cache
  const cacheKey = `native-${chainId}`
  const cached = priceCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.price
  }

  const provider = await getChainProvider(chainConfig.chainId)

  // Get price feed address
  let feedAddress: string | undefined
  if (chainId === 8453) {
    feedAddress = PRICE_FEEDS.base.ETH_USD
  } else if (chainId === 56) {
    feedAddress = PRICE_FEEDS.bnb.BNB_USD
  }

  if (!feedAddress) {
    // Fallback prices if no feed available
    console.warn(`[TokenPriceService] No price feed for chain ${chainId}, using fallback`)
    return chainId === 56 ? 600 : 3000 // Approximate BNB/ETH prices
  }

  try {
    const multicall3Address = getMulticall3Address(chainId)
    if (!multicall3Address) {
      throw new Error(`No Multicall3 address for chain ${chainId}`)
    }

    const multicall = createMulticallService(provider, multicall3Address, chainId)
    const chainlinkIface = new Interface(CHAINLINK_ABI)

    // Batch both Chainlink calls
    const calls = [
      multicall.encodeCall(feedAddress, chainlinkIface, 'latestRoundData', []),
      multicall.encodeCall(feedAddress, chainlinkIface, 'decimals', []),
    ]

    const results = await multicall.callAndDecode<[
      [bigint, bigint, bigint, bigint, bigint],  // latestRoundData returns tuple
      number  // decimals
    ]>(calls)

    const answer = results[0][1]  // answer is second element in latestRoundData tuple
    const decimals = results[1]
    const price = parseFloat(formatUnits(answer, decimals))

    // Cache the result
    priceCache.set(cacheKey, { price, timestamp: Date.now() })

    return price
  } catch (err) {
    console.error('[TokenPriceService] Failed to get native token price:', err)
    // Fallback
    return chainId === 56 ? 600 : 3000
  }
}

// ============================================================================
// V2 Pool Price Calculation
// ============================================================================

async function getV2PoolPrice(
  poolAddress: string,
  tokenAddress: string,
  tokenDecimals: number,
  provider: JsonRpcProvider,
  chainConfig: ChainPriceConfig,
  multicall?: MulticallService
): Promise<number | null> {
  try {
    // Use provided multicall or create one
    let mc = multicall
    if (!mc) {
      const multicall3Address = getMulticall3Address(chainConfig.chainId)
      if (!multicall3Address) {
        console.error(`[TokenPriceService] No Multicall3 address for chain ${chainConfig.chainId}`)
        return null
      }
      mc = createMulticallService(provider, multicall3Address, chainConfig.chainId)
    }

    const v2PoolIface = new Interface(V2_POOL_ABI)

    // Batch getReserves and token0 calls
    const calls = [
      mc.encodeCall(poolAddress, v2PoolIface, 'getReserves', []),
      mc.encodeCall(poolAddress, v2PoolIface, 'token0', []),
    ]

    const results = await mc.callAndDecode<[
      [bigint, bigint, number],  // getReserves returns [reserve0, reserve1, blockTimestampLast]
      string  // token0 address
    ]>(calls)

    const reserves = results[0]
    const token0Address = results[1]

    const reserve0 = reserves[0]
    const reserve1 = reserves[1]

    // Determine which reserve is our token
    const isToken0 = token0Address.toLowerCase() === tokenAddress.toLowerCase()

    // Get the paired token (assumed to be wrapped native)
    const tokenReserve = isToken0 ? reserve0 : reserve1
    const pairedReserve = isToken0 ? reserve1 : reserve0

    // Calculate price in terms of paired token (usually WETH/WBNB)
    // Price = pairedReserve / tokenReserve (adjusted for decimals)
    const tokenReserveFloat = parseFloat(formatUnits(tokenReserve, tokenDecimals))
    const pairedReserveFloat = parseFloat(formatUnits(pairedReserve, chainConfig.nativeToken.decimals))

    if (tokenReserveFloat === 0) return null

    const priceInNative = pairedReserveFloat / tokenReserveFloat

    // Convert to USD
    const nativePriceUsd = await getNativeTokenPriceUsd(chainConfig.chainId)
    return priceInNative * nativePriceUsd
  } catch (err) {
    console.error('[TokenPriceService] V2 pool price fetch failed:', err)
    return null
  }
}

// ============================================================================
// V3 Pool Price Calculation
// ============================================================================

async function getV3PoolPrice(
  poolAddress: string,
  tokenAddress: string,
  tokenDecimals: number,
  provider: JsonRpcProvider,
  chainConfig: ChainPriceConfig,
  dexType: string = 'uniswap-v3',
  multicall?: MulticallService
): Promise<number | null> {
  try {
    // Use provided multicall or create one
    let mc = multicall
    if (!mc) {
      const multicall3Address = getMulticall3Address(chainConfig.chainId)
      if (!multicall3Address) {
        console.error(`[TokenPriceService] No Multicall3 address for chain ${chainConfig.chainId}`)
        return null
      }
      mc = createMulticallService(provider, multicall3Address, chainConfig.chainId)
    }

    // Use the appropriate ABI based on dex type
    const poolAbi = dexType === 'aerodrome-cl' ? AERODROME_CL_POOL_ABI : V3_POOL_ABI
    const v3PoolIface = new Interface(poolAbi)

    // Batch slot0 and token0 calls
    const calls = [
      mc.encodeCall(poolAddress, v3PoolIface, 'slot0', []),
      mc.encodeCall(poolAddress, v3PoolIface, 'token0', []),
    ]

    const results = await mc.callAndDecode<[
      [bigint, number, number, number, number, ...unknown[]],  // slot0 returns tuple (varies by dex)
      string  // token0 address
    ]>(calls)

    const slot0 = results[0]
    const token0Address = results[1]

    const sqrtPriceX96 = slot0[0]

    // Check for zero or invalid sqrtPriceX96
    if (!sqrtPriceX96 || sqrtPriceX96 === 0n) {
      console.warn(`[TokenPriceService] Pool ${poolAddress} has invalid sqrtPriceX96: ${sqrtPriceX96}`)
      return null
    }

    const isToken0 = token0Address.toLowerCase() === tokenAddress.toLowerCase()

    // Calculate price from sqrtPriceX96 using BigInt to preserve precision
    // sqrtPriceX96 = sqrt(token1/token0) * 2^96
    // price = (sqrtPriceX96)^2 / 2^192 = token1_raw / token0_raw
    //
    // Use a precision multiplier to preserve decimal places during integer division
    const sqrtPrice = BigInt(sqrtPriceX96.toString())
    const Q192 = 2n ** 192n
    const PRECISION = 10n ** 36n // 36 decimal places of precision

    // price_raw (scaled) = sqrtPrice^2 * PRECISION / 2^192
    const scaledPriceRaw = (sqrtPrice * sqrtPrice * PRECISION) / Q192

    // Get paired token decimals
    const pairedDecimals = chainConfig.nativeToken.decimals

    // Adjust for token order and decimals
    // price_raw = token1_raw / token0_raw
    // To get price in native per token (human readable):
    let priceInNative: number
    if (isToken0) {
      // Our token is token0, paired (native) is token1
      // price_raw = native_raw / token_raw
      // price_human = price_raw * 10^tokenDecimals / 10^nativeDecimals
      priceInNative = Number(scaledPriceRaw * 10n ** BigInt(tokenDecimals)) / Number(10n ** BigInt(pairedDecimals) * PRECISION)
    } else {
      // Our token is token1, paired (native) is token0
      // price_raw = token_raw / native_raw, need to invert
      // price_human = (1/price_raw) * 10^tokenDecimals / 10^nativeDecimals
      // Invert: PRECISION^2 / scaledPriceRaw gives us inverse with same precision
      const invertedScaled = (PRECISION * PRECISION) / scaledPriceRaw
      priceInNative = Number(invertedScaled * 10n ** BigInt(tokenDecimals)) / Number(10n ** BigInt(pairedDecimals) * PRECISION)
    }

    // Check for invalid price
    if (priceInNative <= 0 || isNaN(priceInNative) || !isFinite(priceInNative)) {
      console.warn(`[TokenPriceService] Pool ${poolAddress} calculated invalid priceInNative: ${priceInNative}`)
      return null
    }

    // Convert to USD
    const nativePriceUsd = await getNativeTokenPriceUsd(chainConfig.chainId)
    const finalPrice = priceInNative * nativePriceUsd

    if (finalPrice <= 0 || isNaN(finalPrice) || !isFinite(finalPrice)) {
      console.warn(`[TokenPriceService] Pool ${poolAddress} calculated invalid USD price: ${finalPrice} (native: ${priceInNative}, nativeUsd: ${nativePriceUsd})`)
      return null
    }

    return finalPrice
  } catch (err) {
    console.error(`[TokenPriceService] V3 pool price fetch failed for ${poolAddress}:`, err)
    return null
  }
}

// ============================================================================
// Main Price Fetching Functions
// ============================================================================

/**
 * Get the price of a token in USD
 */
export async function getTokenPriceUsd(
  symbol: string,
  chainId: number
): Promise<number | null> {
  // Check cache first
  const cached = getCachedPrice(symbol, chainId)
  if (cached !== null) {
    return cached
  }

  const tokenConfig = TOKEN_PRICE_CONFIGS[symbol]
  if (!tokenConfig) {
    console.error(`[TokenPriceService] Unknown token: ${symbol}`)
    return null
  }

  const chainTokenConfig = tokenConfig.chains.find(c => c.chainId === chainId)
  if (!chainTokenConfig) {
    console.error(`[TokenPriceService] Token ${symbol} not configured for chain ${chainId}`)
    return null
  }

  const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
  if (!chainConfig) {
    console.error(`[TokenPriceService] Unknown chain: ${chainId}`)
    return null
  }

  // Get provider
  let provider: JsonRpcProvider
  try {
    provider = await getChainProvider(chainConfig.chainId)
  } catch (err) {
    console.error('[TokenPriceService] Failed to get provider:', err)
    return null
  }

  // Try each pool until we get a price
  for (const pool of chainTokenConfig.pools) {
    // Skip pools with placeholder addresses
    if (pool.address === '0x0000000000000000000000000000000000000000') {
      continue
    }

    let price: number | null = null

    if (pool.dex === 'aerodrome' || pool.dex === 'uniswap-v2' || pool.dex === 'pancakeswap-v2') {
      price = await getV2PoolPrice(
        pool.address,
        chainTokenConfig.address,
        tokenConfig.decimals,
        provider,
        chainConfig
      )
    } else if (pool.dex === 'aerodrome-cl' || pool.dex === 'pancakeswap-v3' || pool.dex === 'uniswap-v3') {
      price = await getV3PoolPrice(
        pool.address,
        chainTokenConfig.address,
        tokenConfig.decimals,
        provider,
        chainConfig,
        pool.dex
      )
    }

    if (price !== null && price > 0) {
      setCachedPrice(symbol, chainId, price)
      return price
    }
  }

  console.warn(`[TokenPriceService] Could not get price for ${symbol} on chain ${chainId}`)
  return null
}

/**
 * Calculate the USD value of a token amount
 */
export async function getTokenValueUsd(
  symbol: string,
  chainId: number,
  amount: bigint,
  decimals: number
): Promise<number | null> {
  const price = await getTokenPriceUsd(symbol, chainId)
  if (price === null) return null

  const amountFloat = parseFloat(formatUnits(amount, decimals))
  return amountFloat * price
}

/**
 * Format a USD value for display
 */
export function formatUsdValue(value: number | null): string {
  if (value === null) return '$—'
  if (value === 0) return '$0.00'
  if (value < 0.01) return '<$0.01'

  // Truncate to 2 decimal places (don't round up)
  const truncated = Math.floor(value * 100) / 100
  return `$${truncated.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Hook-friendly price fetcher that returns loading state
 */
export interface PriceResult {
  price: number | null
  loading: boolean
  error: string | null
}

// Clear the price cache (useful for testing or forcing refresh)
export function clearPriceCache(): void {
  priceCache.clear()
  clearProviderCache()
}
